import moment from 'moment'
import { promisify } from '../../../Utils/Actions'
import { getOverlayBoundary } from '../../../Components/DrawMap/util'
import {
  getPolygonCentreAndDelta,
  getDistance,
} from '../../../shared/libraries/geo'
import { createCityPlansGQLHandler } from '../CityPlanCreate/util'
import {
  MISSION_ENUM_TYPES,
  CAMPAIGN_ENUM_TYPES,
  CITY_STATUS_ENUM_ACTIVE,
  CAMPAIGN_LOG_ENUM_TYPES,
} from '../../../shared/constants'
import { createMissions, getCityPlans } from '../../../Module/graphQL'
import {
  getWeekNumberAndYear,
  getDateOfISOWeek,
  formatToLocalDate,
} from '../../../shared/libraries/utils/date'
import { normalizeGraphQLConnection } from '../../../shared/graphql/utils'
import { generateRandomColor } from '../../../Utils/Misc'
import { createCampaignDeliverabilityByCampaignId } from './gql'
import { onCreateXeroItem } from './util'
import { insertCampaignLogs } from '../../../Logs/campaign/gql'

/**
 * Splits overlays over closes cities
 * Creates city plans for those cities
 * Then creates all missions
 * @param {number} campaignId
 * @param {object} overlaysMap Selected overlays that should become LB missions (GMAP.Overlay)
 * @param {array} cities All cities
 *
 * @returns {object} Object containing created city plans and created missions
 */
export const createMissionsFromCampaignBuilderOverlays = async ({
  campaignId,
  overlaysMap,
  cities: citiesProps,
}) => {
  const cities = citiesProps.filter(
    city => city.statusV2 === CITY_STATUS_ENUM_ACTIVE,
  )
  const processedOverlaysMap = Object.values(overlaysMap).reduce(
    (acc, overlay) => {
      acc[overlay.customId] = {
        id: overlay.customId,
        payload: overlay.payload,
        boundary: getOverlayBoundary(overlay),
      }
      return acc
    },
    {},
  )

  const overlaysByCityMap = sortOverlaysByDistanceToCity({
    overlaysMap: processedOverlaysMap,
    cities,
  })

  const response = await getCityPlans({ campaignId })
  const { data } = await response.json()
  const existingCityPlans = normalizeGraphQLConnection(data.cityPlans)
  const citiesWithCityPlans = existingCityPlans.map(({ city }) => city.id)

  const cityPlansInput = Object.keys(overlaysByCityMap).reduce(
    (acc, cityId) => {
      if (!citiesWithCityPlans.includes(cityId)) {
        acc.push({
          campaignId,
          cityId,
        })
      }
      return acc
    },
    [],
  )

  const createdCityPlans = await createCityPlansGQLHandler(cityPlansInput)

  const missionsInput = getMissionsInput({
    cityPlans: [...existingCityPlans, ...createdCityPlans],
    overlaysByCityMap,
  })

  const createdMissions = await createMissions({
    missions: missionsInput,
    campaignId,
  })

  return {
    cityPlans: createdCityPlans,
    missions: createdMissions,
  }
}

/**
 * [Pure]
 * Creates input that will be used to create missions, based on mapped overlays by city and received city plans
 * @param {array} cityPlans Campaign's city plans
 * @param {object} overlaysByCityMap Map where key is the city id and value is the array of boundaries that will become missions
 * @param {function} generateBoundaryColor Function, that returns boundary color
 *
 * @returns {array} CreateMissionsInput
 */
export const getMissionsInput = ({
  cityPlans,
  overlaysByCityMap,
  generateBoundaryColor = generateRandomColor,
} = {}) => {
  if (!cityPlans || !overlaysByCityMap) {
    throw new Error('Both arguments are mandatory in getMissionsInput()')
  }

  return cityPlans.reduce((acc, cityPlan) => {
    const { id: cityPlanId, city } = cityPlan
    const { id: cityId } = city

    // If there is no new missions for the city plan - skip it
    if (!overlaysByCityMap[cityId]) return acc

    overlaysByCityMap[cityId].forEach(boundary => {
      const randomColor = generateBoundaryColor()

      let temp = new Date()
      temp.setDate(temp.getDate() + 7)
      const { weekNumber, year } = getWeekNumberAndYear(temp)
      temp = getDateOfISOWeek(weekNumber + 1, year)
      temp.setMilliseconds(temp.getMilliseconds() - 1)
      const endOfTheNextWeek = formatToLocalDate(temp)

      const createMissionInput = {
        cityPlanId: Number(cityPlanId),
        type: MISSION_ENUM_TYPES.LETTERBOX,
        dateTime: endOfTheNextWeek,
        boundary: boundary.boundary,
        label:
          boundary.payload && boundary.payload.label
            ? boundary.payload.label
            : 'Unnamed_area',
        boundaryColour: randomColor,
      }

      acc.push(createMissionInput)
    })

    return acc
  }, [])
}

export const convertPolygonToPoint = function(polygon) {
  var coordinates = getPolygonCentreAndDelta(
    polygon.geometry.coordinates[0].map(c => ({
      latitude: c[1],
      longitude: c[0],
    })),
  )
  return {
    type: 'Feature',
    geometry: {
      type: 'point',
      coordinates: [coordinates.longitude, coordinates.latitude],
    },
  }
}

/**
 * Get the closest point in an array to an original point
 * @param {Point} point
 * @param {Array<Point>} points
 * @returns {Point}
 */
export const closestPoint = function(point, points) {
  if (
    typeof point.coordinates[0] !== 'number' ||
    typeof point.coordinates[1] !== 'number' ||
    isNaN(point.coordinates[0]) ||
    isNaN(point.coordinates[1])
  )
    throw new Error('Coordinates is not valid for point.')
  var closestPoint = null,
    closestPointDistance = null

  points.forEach(lPoint => {
    const distanceBetween = getDistance(
      point.coordinates[1],
      point.coordinates[0],
      lPoint.coordinates[1],
      lPoint.coordinates[0],
    )
    if (!closestPoint || distanceBetween < closestPointDistance) {
      closestPoint = lPoint
      closestPointDistance = distanceBetween
    }
  })
  return closestPoint
}

/**
 * [Pure]
 * Assigns each overlay to the closest city,
 * returns map where key is the city id and value is the array of the overlays for that city
 * @param {object} overlaysMap Overlays map where key is overlay's id nad value is overlay
 * @param {array} cities Array of cities
 *
 * @returns {object} Map where key is the city id and value is the array of the overlays for that city
 * @example
 * sortOverlaysByDistanceToCity({
 *    overlaysMap: { 785: GMAP.OVERLAY },
 *    cities: [{
 *      id: 21,
 *      location: { latitude: 1, longitude: 2}
 *    }]
 *  })
 */
export const sortOverlaysByDistanceToCity = ({ overlaysMap, cities } = {}) => {
  if (!overlaysMap || !cities) {
    throw new Error(
      'Both arguments are mandatory in sortOverlaysByDistanceToCity()',
    )
  }

  // Get overlays centres array
  const overlaysCentres = Object.values(overlaysMap).map(overlay => {
    const centreAndDelta = getPolygonCentreAndDelta(overlay.boundary)
    return {
      id: overlay.id,
      latitude: centreAndDelta.latitude,
      longitude: centreAndDelta.longitude,
    }
  })

  // Get cities centres array
  const cityCentres = cities.map(city => ({
    id: city.id,
    latitude: city.location.latitude,
    longitude: city.location.longitude,
  }))

  return overlaysCentres.reduce((acc, overlayCentreData) => {
    // Get id of the closest city
    const { closestCityId } = cityCentres.reduce(
      (acc, cityCentreData) => {
        // Get distance between city centre and overlay centre
        const distanceBetween = getDistance(
          cityCentreData.latitude,
          cityCentreData.longitude,
          overlayCentreData.latitude,
          overlayCentreData.longitude,
        )
        // If there is no "distanceBetween" or current "distanceBetween" is less then previous one then
        // save this city and distance as closest
        if (!acc.distanceBetween || distanceBetween < acc.distanceBetween) {
          return {
            closestCityId: cityCentreData.id,
            distanceBetween,
          }
        }
        return acc
      },
      {
        closestCityId: null,
        distanceBetween: null,
      },
    )

    const overlay = overlaysMap[overlayCentreData.id]

    if (!Array.isArray(acc[closestCityId])) {
      acc[closestCityId] = []
    }

    acc[closestCityId].push(overlay)

    return acc
  }, {})
}

/**
 * Sets uploads pitch file and saves it against the campaign
 * @param {number} campaignId
 * @param {file} pitchFile
 * @param {object} props Component's props
 */
const updateCampaignWithPitchFile = async ({
  campaignId,
  pitchFile,
  props,
}) => {
  const { updateCampaign, uploadPitchFile: uploadPitchFileFunction } = props
  const uploadPitchFile = promisify(uploadPitchFileFunction)
  const pitchUrl = await uploadPitchFile(campaignId, pitchFile)
  await updateCampaign({
    campaignId,
    pitchUrl,
  })
}

/**
 * Contains side effects calls for campaign create operation
 * @param {number} campaignId
 * @param {object} values Form values
 * @param {object} props Component's props
 */
export const runCreateCampaignSideEffects = async ({
  campaign,
  values,
  props,
  parsedValues,
  isXeroAvailable,
}) => {
  const { xeroItemCodeForCreateCreateData } = values

  const campaignId = campaign.id

  await createCampaignDeliverabilityByCampaignId(campaign.id)

  const newMetrics = parsedValues.metrics.reduce((acc, item) => {
    if (!item.id) {
      const newMetric = {
        ...item,
        targetCostPerMetric: item.targetCostPerMetric / 100 / 100,
        targetConversionRate: item.targetConversionRate * 100,
      }

      delete newMetric.id

      acc.push(newMetric)
    }
    return acc
  }, [])

  const logs = []

  if (newMetrics.length) {
    logs.push({
      campaignId: campaign.id,
      type: CAMPAIGN_LOG_ENUM_TYPES.ADD_METRIC,
      additionalInfo: `CAMPAIGN CREATE value: ${newMetrics
        .map(item => JSON.stringify(item))
        .join(', ')}`,
    })
  }

  if (values.name) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_NAME,
      additionalInfo: `CAMPAIGN CREATE value: ${values.name}`,
    })
  }

  if (values.status) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_STATUS,
      additionalInfo: `CAMPAIGN CREATE value: ${values.status}`,
    })
  }

  if (values.startDate) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_START_DATE,
      additionalInfo: `CAMPAIGN CREATE value: ${moment(values.startDate).format(
        'YYYY-MM-DD',
      )}`,
    })
  }

  if (values.offerExpirationDate) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_OFFER_EXPIRATION_DATE,
      additionalInfo: `CAMPAIGN CREATE value: ${moment(
        values.offerExpirationDate,
      ).format('YYYY-MM-DD')}`,
    })
  }

  if (values.printingCostPerFlyer) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_COST_PER_FLYER,
      additionalInfo: `CAMPAIGN CREATE value: ${values.printingCostPerFlyer /
        100}`,
    })
  }

  if (values.baRedemptionPeriod) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_REDEMPTION_TIME,
      additionalInfo: `CAMPAIGN CREATE value: ${values.baRedemptionPeriod}`,
    })
  }

  if (values.campaignDurationWeek) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_DURATION,
      additionalInfo: `CAMPAIGN CREATE value: ${values.campaignDurationWeek}`,
    })
  }

  if (typeof values.noExpiration === 'boolean') {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_EXPIRATION,
      additionalInfo: `CAMPAIGN CREATE value: ${values.noExpiration}`,
    })
  }

  if (values.bonusLimit) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_BONUS_ALLOWANCE,
      additionalInfo: `CAMPAIGN CREATE value: ${values.bonusLimit}`,
    })
  }

  if (values.payoutPerConversion) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_BONUS_PER_CONVERSION,
      additionalInfo: `CAMPAIGN CREATE value: ${values.payoutPerConversion}`,
    })
  }

  if (typeof values.conversionsApiEnabled === 'boolean') {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_CONVERSION_API,
      additionalInfo: `CAMPAIGN CREATE value: ${values.conversionsApiEnabled}`,
    })
  }
  if (typeof values.scanDataEnabled === 'boolean') {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_SCAN_DATA,
      additionalInfo: `CAMPAIGN CREATE value: ${values.scanDataEnabled}`,
    })
  }

  if (typeof values.receiveReport === 'boolean') {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_RECEIVE_REPORT,
      additionalInfo: `CAMPAIGN CREATE value: ${values.receiveReport}`,
    })
  }

  if (logs.length) {
    await insertCampaignLogs(logs)
  }

  if (values.pitchUrl instanceof Array) {
    const pitchFile = values.pitchUrl[0]
    await updateCampaignWithPitchFile({
      campaignId: campaign.id,
      pitchFile,
      props,
    })
  }
  if (
    campaign.type === CAMPAIGN_ENUM_TYPES.LETTERBOX &&
    values.overlaysMap &&
    Object.keys(values.overlaysMap).length
  ) {
    await createMissionsFromCampaignBuilderOverlays({
      campaignId: campaign.id,
      overlaysMap: values.overlaysMap,
      cities: props.cities,
    })
  }
  if (xeroItemCodeForCreateCreateData && isXeroAvailable) {
    await onCreateXeroItem({
      campaign: campaign,
      countryCode: xeroItemCodeForCreateCreateData.countryCode,
      tenantId: xeroItemCodeForCreateCreateData.tenantId,
    })
  }
}

/**
 * Contains side effects calls for campaign update operation
 * @param {number} campaignId
 * @param {object} values Form values
 * @param {object} props Component's props
 */
export const runUpdateCampaignSideEffects = async ({
  campaign,
  values,
  props,
  oldMetrics,
  parsedValues,
  primaryMetricIndex,
}) => {
  const deletedMetrics = oldMetrics.reduce((acc, item) => {
    const isDeleted = !parsedValues.metrics.some(
      newItem => newItem.id === item.id,
    )

    if (isDeleted || item.id === null || item.id === undefined) {
      acc.push({
        ...item,
        targetConversionRate: item.targetConversionRate * 100,
        targetCostPerMetric: item.targetCostPerMetric / 100,
      })
    }

    return acc
  }, [])

  const newMetrics = parsedValues.metrics.reduce((acc, item) => {
    if (!item.id) {
      const newMetric = {
        ...item,
        targetCostPerMetric: item.targetCostPerMetric / 100 / 100,
        targetConversionRate: item.targetConversionRate * 100,
      }
      delete newMetric.id
      acc.push(newMetric)
    }
    return acc
  }, [])

  const editedMetrics = oldMetrics.reduce((acc, item) => {
    if (item.id) {
      const thisMetricUpdated = parsedValues.metrics.find(
        metric => metric.id === item.id,
      )
      if (thisMetricUpdated) {
        const result = []
        const formattedMetric = {
          ...thisMetricUpdated,
          targetCostPerMetric: thisMetricUpdated.targetCostPerMetric / 100,
        }

        if (item.name !== formattedMetric.name) {
          result.push(
            `Name changed from "${item.name}" to "${formattedMetric.name}"`,
          )
        }
        if (item.label !== formattedMetric.label) {
          result.push(
            `Label changed from "${item.label}" to "${formattedMetric.label}"`,
          )
        }
        if (
          item.targetConversionRate !== formattedMetric.targetConversionRate
        ) {
          result.push(
            `Target conversion rate changed from "${item.targetConversionRate *
              100}" to "${formattedMetric.targetConversionRate * 100}"`,
          )
        }
        if (item.targetCostPerMetric !== formattedMetric.targetCostPerMetric) {
          result.push(
            `Target cost per metric changed from "${item.targetCostPerMetric /
              100}" to "${formattedMetric.targetCostPerMetric / 100}"`,
          )
        }
        if (result.length) {
          const string = `Metric ID:${item.id} - [${result.join(', ')}]`
          acc.push(string)
        }
      }
    }
    return acc
  }, [])

  const logs = []
  if (deletedMetrics.length) {
    logs.push({
      campaignId: campaign.id,
      type: CAMPAIGN_LOG_ENUM_TYPES.DELETE_METRIC,
      additionalInfo: `${deletedMetrics
        .map(item => JSON.stringify(item))
        .join(', ')}`,
    })
  }
  if (newMetrics.length) {
    logs.push({
      campaignId: campaign.id,
      type: CAMPAIGN_LOG_ENUM_TYPES.ADD_METRIC,
      additionalInfo: `${newMetrics
        .map(item => JSON.stringify(item))
        .join(', ')}`,
    })
  }

  if (editedMetrics.length) {
    editedMetrics.forEach(editedMetric => {
      logs.push({
        campaignId: campaign.id,
        type: CAMPAIGN_LOG_ENUM_TYPES.EDIT_METRIC,
        additionalInfo: `${editedMetric}`,
      })
    })
  }

  if (primaryMetricIndex) {
    const oldPrimaryMetric = oldMetrics[0]
    const newPrimaryMetric = parsedValues.metrics[primaryMetricIndex]
    if (newPrimaryMetric.id) {
      if (newPrimaryMetric.id !== oldPrimaryMetric.id) {
        logs.push({
          campaignId: campaign.id,
          type: CAMPAIGN_LOG_ENUM_TYPES.CHANGE_PRIMARY_METRIC,
          additionalInfo: `Primary metric changed from "${
            oldPrimaryMetric.name
          }" to "${newPrimaryMetric.name}"`,
        })
      }
    } else {
      if (newPrimaryMetric.name !== oldPrimaryMetric.name) {
        logs.push({
          campaignId: campaign.id,
          type: CAMPAIGN_LOG_ENUM_TYPES.CHANGE_PRIMARY_METRIC,
          additionalInfo: `Primary metric changed from "${
            oldPrimaryMetric.name
          }" to "${newPrimaryMetric.name}"`,
        })
      }
    }
  }
  if (logs.length) {
    await insertCampaignLogs(logs)
  }
  if (
    campaign.type === CAMPAIGN_ENUM_TYPES.LETTERBOX &&
    values.overlaysMap &&
    Object.keys(values.overlaysMap).length
  ) {
    await createMissionsFromCampaignBuilderOverlays({
      campaignId: campaign.id,
      overlaysMap: values.overlaysMap,
      cities: props.cities,
    })
  }
}
